import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { ALL_DIRECTIONS, DIAGONALS, KNIGHT_STEPS, ORTHOGONALS, fileLabel, isDarkSquare, label, parseLabel, ray, rankLabel, stepFrom, xOf, yOf } from '@campfire/grid';

export const SIZE = 8;
/** Fifty-move rule: 100 plies without a pawn move or capture end the game automatically (no claim needed). */
export const FIFTY_MOVE_PLIES = 100;
export const SEAT_NAMES = ['White', 'Black'] as const;

/** Piece types. A square holds 0 (empty) or `type + 6 * seat`: white 1-6, black 7-12. */
export const PAWN = 1;
export const KNIGHT = 2;
export const BISHOP = 3;
export const ROOK = 4;
export const QUEEN = 5;
export const KING = 6;
export const piece = (seat: Seat, type: number): number => type + 6 * seat;
export const ownerOf = (p: number): Seat | null => (p === 0 ? null : Math.floor((p - 1) / 6));
export const typeOf = (p: number): number => (p === 0 ? 0 : ((p - 1) % 6) + 1);
const LETTERS = ' PNBRQK';

/** Castling rights bit mask. */
export const WK = 1;
export const WQ = 2;
export const BK = 4;
export const BQ = 8;

export type ChessOptions = Record<string, never>;
export type Promo = 'Q' | 'R' | 'B' | 'N';
const PROMO_TYPE: Record<Promo, number> = { Q: QUEEN, R: ROOK, B: BISHOP, N: KNIGHT };
const PROMOS: readonly Promo[] = ['Q', 'R', 'B', 'N'];
const promoLetter = (t: number): Promo => LETTERS[t] as Promo;

export type MoveKind = 'quiet' | 'double' | 'ep' | 'castle';
export interface Move {
  readonly from: number;
  readonly to: number;
  /** Moving piece type. */
  readonly type: number;
  /** Promotion piece type, or 0. */
  readonly promo: number;
  /** Captured piece type, or 0. */
  readonly capture: number;
  readonly kind: MoveKind;
}

export interface ChessState {
  readonly options: ChessOptions;
  readonly board: readonly number[];
  readonly turn: Seat;
  /** Castling rights bit mask (WK | WQ | BK | BQ). */
  readonly castling: number;
  /** En passant target square (the square a pawn skipped over), set after every double step. */
  readonly ep: number | null;
  /** Plies since the last pawn move or capture. */
  readonly halfmove: number;
  readonly moves: number;
  /** Position keys since the last irreversible move, the current one last (for repetition). */
  readonly history: readonly string[];
  readonly sans: readonly string[];
  readonly startTurn: Seat;
  readonly last: { readonly from: number; readonly to: number } | null;
  /** Piece types each seat has captured. */
  readonly captured: readonly [readonly number[], readonly number[]];
  readonly drawOffer: Seat | null;
  readonly result: Outcome | null;
}

export type ChessAction =
  | { readonly type: 'move'; readonly from: number; readonly to: number; readonly promo?: Promo }
  | { readonly type: 'offerDraw' }
  | { readonly type: 'acceptDraw' }
  | { readonly type: 'declineDraw' }
  | { readonly type: 'resign' };

export interface ChessView {
  readonly you: Seat | null;
  readonly board: readonly number[];
  readonly turn: Seat | null;
  /** Every legal move for the viewer when it is their move (promotions listed once per piece); empty otherwise. */
  readonly legal: readonly { readonly from: number; readonly to: number; readonly promo?: Promo }[];
  readonly last: { readonly from: number; readonly to: number } | null;
  /** Square of the king of the side to move when it is in check. */
  readonly check: number | null;
  readonly sans: readonly string[];
  readonly startTurn: Seat;
  readonly captured: readonly [readonly number[], readonly number[]];
  readonly drawOffer: Seat | null;
  readonly halfmove: number;
  readonly moves: number;
  readonly result: Outcome | null;
}

const other = (s: Seat): Seat => 1 - s;
const fwd = (s: Seat): number => (s === 0 ? 1 : -1);

export function initialBoard(): number[] {
  const b = new Array<number>(64).fill(0);
  const back = [ROOK, KNIGHT, BISHOP, QUEEN, KING, BISHOP, KNIGHT, ROOK];
  for (let x = 0; x < 8; x++) {
    b[x] = piece(0, back[x] as number);
    b[8 + x] = piece(0, PAWN);
    b[48 + x] = piece(1, PAWN);
    b[56 + x] = piece(1, back[x] as number);
  }
  return b;
}

/** Is `square` attacked by any piece of `by`? */
export function attacked(board: readonly number[], square: number, by: Seat): boolean {
  const at = (i: number | null, t: number) => i !== null && board[i] === piece(by, t);
  for (const dx of [-1, 1]) if (at(stepFrom(SIZE, square, [dx, -fwd(by)]), PAWN)) return true;
  for (const d of KNIGHT_STEPS) if (at(stepFrom(SIZE, square, d), KNIGHT)) return true;
  for (const d of ALL_DIRECTIONS) if (at(stepFrom(SIZE, square, d), KING)) return true;
  for (const [dirs, t] of [[ORTHOGONALS, ROOK], [DIAGONALS, BISHOP]] as const) {
    for (const d of dirs) {
      for (const i of ray(SIZE, square, d)) {
        const p = board[i] as number;
        if (p === 0) continue;
        if (p === piece(by, t) || p === piece(by, QUEEN)) return true;
        break;
      }
    }
  }
  return false;
}

export const kingSquare = (board: readonly number[], seat: Seat): number => board.indexOf(piece(seat, KING));
export const inCheck = (board: readonly number[], seat: Seat): boolean => {
  const k = kingSquare(board, seat);
  return k >= 0 && attacked(board, k, other(seat));
};

interface Position {
  readonly board: readonly number[];
  readonly turn: Seat;
  readonly castling: number;
  readonly ep: number | null;
}

function pseudoMoves(p: Position): Move[] {
  const { board, turn, castling, ep } = p;
  const out: Move[] = [];
  const push = (from: number, to: number, type: number, kind: MoveKind = 'quiet', promo = 0, capture = typeOf(board[to] as number)) => out.push({ from, to, type, promo, capture, kind });
  for (let from = 0; from < 64; from++) {
    const pc = board[from] as number;
    if (ownerOf(pc) !== turn) continue;
    const type = typeOf(pc);
    if (type === PAWN) {
      const last = turn === 0 ? 7 : 0;
      const add = (to: number, kind: MoveKind, capture?: number) => {
        if (yOf(SIZE, to) === last) for (const t of [QUEEN, ROOK, BISHOP, KNIGHT]) push(from, to, PAWN, kind, t, capture);
        else push(from, to, PAWN, kind, 0, capture);
      };
      const one = stepFrom(SIZE, from, [0, fwd(turn)]);
      if (one !== null && board[one] === 0) {
        add(one, 'quiet');
        const two = stepFrom(SIZE, from, [0, 2 * fwd(turn)]);
        if (yOf(SIZE, from) === (turn === 0 ? 1 : 6) && two !== null && board[two] === 0) push(from, two, PAWN, 'double');
      }
      for (const dx of [-1, 1]) {
        const to = stepFrom(SIZE, from, [dx, fwd(turn)]);
        if (to === null) continue;
        if (ownerOf(board[to] as number) === other(turn)) add(to, 'quiet');
        else if (to === ep) push(from, to, PAWN, 'ep', 0, PAWN);
      }
    } else if (type === KNIGHT || type === KING) {
      for (const d of type === KNIGHT ? KNIGHT_STEPS : ALL_DIRECTIONS) {
        const to = stepFrom(SIZE, from, d);
        if (to !== null && ownerOf(board[to] as number) !== turn) push(from, to, type);
      }
    } else {
      const dirs = type === ROOK ? ORTHOGONALS : type === BISHOP ? DIAGONALS : ALL_DIRECTIONS;
      for (const d of dirs) {
        for (const to of ray(SIZE, from, d)) {
          const o = ownerOf(board[to] as number);
          if (o !== turn) push(from, to, type);
          if (o !== null) break;
        }
      }
    }
  }
  // Castling: king and rook unmoved, squares between empty, king not in, through or into check.
  const home = turn === 0 ? 4 : 60;
  if (board[home] === piece(turn, KING) && !attacked(board, home, other(turn))) {
    const kingSide = turn === 0 ? WK : BK;
    const queenSide = turn === 0 ? WQ : BQ;
    const free = (...ss: number[]) => ss.every((i) => board[i] === 0);
    const safe = (...ss: number[]) => ss.every((i) => !attacked(board, i, other(turn)));
    if (castling & kingSide && board[home + 3] === piece(turn, ROOK) && free(home + 1, home + 2) && safe(home + 1, home + 2)) push(home, home + 2, KING, 'castle');
    if (castling & queenSide && board[home - 4] === piece(turn, ROOK) && free(home - 1, home - 2, home - 3) && safe(home - 1, home - 2)) push(home, home - 2, KING, 'castle');
  }
  return out;
}

/** The board after `m` (no legality check). */
export function boardAfter(board: readonly number[], m: Move, seat: Seat): number[] {
  const b = board.slice();
  b[m.from] = 0;
  b[m.to] = m.promo ? piece(seat, m.promo) : piece(seat, m.type);
  if (m.kind === 'ep') b[m.to - 8 * fwd(seat)] = 0;
  if (m.kind === 'castle') {
    const king = m.to > m.from;
    b[king ? m.from + 3 : m.from - 4] = 0;
    b[king ? m.from + 1 : m.from - 1] = piece(seat, ROOK);
  }
  return b;
}

/** Every fully legal move for the side to move. */
export function legalMoves(p: Position): Move[] {
  return pseudoMoves(p).filter((m) => !inCheck(boardAfter(p.board, m, p.turn), p.turn));
}

const RIGHTS_LOST: Readonly<Record<number, number>> = { 0: WQ, 7: WK, 4: WK | WQ, 56: BQ, 63: BK, 60: BK | BQ };
const rightsAfter = (castling: number, m: Move): number => castling & ~((RIGHTS_LOST[m.from] ?? 0) | (RIGHTS_LOST[m.to] ?? 0));

/** Position identity for repetition: placement, side to move, castling rights and a capturable en passant square. */
export function positionKey(p: Position, legal: readonly Move[]): string {
  const epLegal = p.ep !== null && legal.some((m) => m.kind === 'ep');
  return `${p.board.join('')}|${p.turn}|${p.castling}|${epLegal ? p.ep : '-'}`;
}

/** Dead position by material: K v K, K+minor v K, or only bishops all on one colour. */
export function insufficientMaterial(board: readonly number[]): boolean {
  const rest = board.map((p, i) => ({ t: typeOf(p), i })).filter((x) => x.t !== 0 && x.t !== KING);
  if (rest.length <= 1) return rest.every((x) => x.t === KNIGHT || x.t === BISHOP);
  return rest.every((x) => x.t === BISHOP) && rest.every((x) => isDarkSquare(SIZE, x.i) === isDarkSquare(SIZE, (rest[0] as { i: number }).i));
}

export const CASTLE_FLAGS = 'KQkq';

/** Loads a position from FEN (the move counters are optional). Throws on malformed text. */
export function fromFen(fen: string): ChessState {
  const [placement, side = 'w', rights = '-', epText = '-', half = '0'] = fen.trim().split(/\s+/);
  const rows = (placement ?? '').split('/');
  if (rows.length !== 8) throw new Error('FEN needs 8 ranks');
  const board = new Array<number>(64).fill(0);
  rows.forEach((row, r) => {
    let x = 0;
    for (const ch of row) {
      if (/[1-8]/.test(ch)) x += Number(ch);
      else {
        const t = LETTERS.indexOf(ch.toUpperCase());
        if (t < 1 || x > 7) throw new Error(`bad FEN piece ${ch}`);
        board[(7 - r) * 8 + x++] = piece(ch === ch.toUpperCase() ? 0 : 1, t);
      }
    }
    if (x !== 8) throw new Error('FEN rank must have 8 files');
  });
  if (side !== 'w' && side !== 'b') throw new Error('FEN side must be w or b');
  const turn: Seat = side === 'w' ? 0 : 1;
  let castling = 0;
  if (rights !== '-') for (const ch of rights) castling |= 1 << CASTLE_FLAGS.indexOf(ch);
  const ep = epText === '-' ? null : parseLabel(SIZE, epText);
  const pos = { board, turn, castling, ep };
  return {
    options: {},
    ...pos,
    halfmove: Number(half) || 0,
    moves: 0,
    history: [positionKey(pos, legalMoves(pos))],
    sans: [],
    startTurn: turn,
    last: null,
    captured: [[], []],
    drawOffer: null,
    result: null,
  };
}

export function toFen(s: Pick<ChessState, 'board' | 'turn' | 'castling' | 'ep' | 'halfmove' | 'moves'>): string {
  const rows: string[] = [];
  for (let y = 7; y >= 0; y--) {
    let row = '';
    let gap = 0;
    for (let x = 0; x < 8; x++) {
      const p = s.board[y * 8 + x] as number;
      if (p === 0) gap++;
      else {
        row += (gap ? gap : '') + (ownerOf(p) === 0 ? (LETTERS[typeOf(p)] as string) : (LETTERS[typeOf(p)] as string).toLowerCase());
        gap = 0;
      }
    }
    rows.push(row + (gap ? gap : ''));
  }
  const rights = [...CASTLE_FLAGS].filter((_, i) => s.castling & (1 << i)).join('') || '-';
  return `${rows.join('/')} ${s.turn === 0 ? 'w' : 'b'} ${rights} ${s.ep === null ? '-' : label(SIZE, s.ep)} ${s.halfmove} ${Math.floor(s.moves / 2) + 1}`;
}

/** Counts leaf nodes of the legal move tree (promotions count as separate moves). */
export function perft(s: Position, depth: number): number {
  if (depth === 0) return 1;
  const moves = legalMoves(s);
  if (depth === 1) return moves.length;
  let n = 0;
  for (const m of moves) {
    const board = boardAfter(s.board, m, s.turn);
    n += perft({ board, turn: other(s.turn), castling: rightsAfter(s.castling, m), ep: m.kind === 'double' ? (m.from + m.to) / 2 : null }, depth - 1);
  }
  return n;
}

const startMatches = (m: Move, a: { from: number; to: number; promo?: Promo | undefined }): boolean => m.from === a.from && m.to === a.to && (m.promo === 0 || a.promo === undefined || PROMO_TYPE[a.promo] === m.promo);

/** Standard algebraic notation for `m` in the position `p`, without the check suffix. */
export function sanOf(m: Move, legal: readonly Move[]): string {
  if (m.kind === 'castle') return m.to > m.from ? 'O-O' : 'O-O-O';
  const target = label(SIZE, m.to);
  const promo = m.promo ? `=${LETTERS[m.promo]}` : '';
  if (m.type === PAWN) return `${m.capture ? `${fileLabel(xOf(SIZE, m.from))}x` : ''}${target}${promo}`;
  const rivals = legal.filter((o) => o.type === m.type && o.to === m.to && o.from !== m.from);
  let dis = '';
  if (rivals.length > 0) {
    if (rivals.every((o) => xOf(SIZE, o.from) !== xOf(SIZE, m.from))) dis = fileLabel(xOf(SIZE, m.from));
    else if (rivals.every((o) => yOf(SIZE, o.from) !== yOf(SIZE, m.from))) dis = rankLabel(yOf(SIZE, m.from));
    else dis = label(SIZE, m.from);
  }
  return `${LETTERS[m.type]}${dis}${m.capture ? 'x' : ''}${target}`;
}

const scoresFor = (winner: Seat | null): Record<number, number> => (winner === null ? { 0: 0.5, 1: 0.5 } : { [winner]: 1, [other(winner)]: 0 });
const win = (seat: Seat, reason: string): Outcome => ({ winners: [seat], draw: false, reason, scores: scoresFor(seat) });
const draw = (reason: string): Outcome => ({ winners: [], draw: true, reason, scores: scoresFor(null) });

export const chess: GameDefinition<ChessState, ChessAction, ChessView, ChessOptions> = {
  id: 'chess',
  name: 'Chess',
  rules: [
    'Chess for two players. White moves first. Tap one of your pieces to see where it can legally go (captures are marked differently), then tap the destination. The app enforces every rule, including check.',
    'Pawns step forward one square (two from their start square) and capture diagonally; en passant is allowed. A pawn reaching the far rank must promote: choose a queen, rook, bishop or knight from the picker.',
    'Castling: the king moves two squares toward a rook that has not moved. It is not allowed if the king or that rook has moved, if a piece is in between, or if the king is in check or would pass through or land on an attacked square. Tap the king, then the square two away.',
    'You must get out of check. Checkmate (no legal move while in check) wins the game.',
    'Draws: stalemate (no legal move but not in check); insufficient material (K v K, K + one bishop or knight v K, or bishops that are all on one colour); threefold repetition of the same position (automatic); and the 50-move rule, which is automatic here: the game is drawn after 50 moves by each side (100 plies) without a pawn move or capture. Nobody has to claim them.',
    'Either player can offer a draw at any time and the other accepts or declines (making a move declines it). You can resign at any time; it asks you to confirm with a second tap.',
    'The move list is shown in standard algebraic notation (Nf3, exd5, O-O, e8=Q+, Qxf7#).',
  ],
  minSeats: 2,
  maxSeats: 2,
  defaultOptions: {},

  setup: () => {
    const pos = { board: initialBoard(), turn: 0 as Seat, castling: WK | WQ | BK | BQ, ep: null };
    return {
      options: {},
      ...pos,
      halfmove: 0,
      moves: 0,
      history: [positionKey(pos, legalMoves(pos))],
      sans: [],
      startTurn: 0,
      last: null,
      captured: [[], []],
      drawOffer: null,
      result: null,
    };
  },

  // Either player may resign or answer a draw offer at any time; only the mover's move is due.
  activeSeats: (s) => (s.result ? [] : [0, 1]),
  waitingOn: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (s.result) return { ok: false, reason: 'game over' };
    if (seat !== 0 && seat !== 1) return { ok: false, reason: 'not a player' };
    switch (a.type) {
      case 'move': {
        if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
        if (!Number.isInteger(a.from) || !Number.isInteger(a.to) || a.from < 0 || a.from > 63 || a.to < 0 || a.to > 63) return { ok: false, reason: 'a move is two squares' };
        if (a.promo !== undefined && !PROMOS.includes(a.promo)) return { ok: false, reason: 'promote to Q, R, B or N' };
        const legal = legalMoves(s).filter((m) => m.from === a.from && m.to === a.to);
        if (legal.length === 0) {
          if (ownerOf(s.board[a.from] as number) !== seat) return { ok: false, reason: 'start from one of your pieces' };
          return { ok: false, reason: inCheck(s.board, seat) ? 'illegal move: your king is in check' : 'illegal move' };
        }
        if ((legal[0] as Move).promo) return a.promo === undefined ? { ok: false, reason: 'choose a promotion piece' } : { ok: true };
        return a.promo === undefined ? { ok: true } : { ok: false, reason: 'only a pawn reaching the last rank promotes' };
      }
      case 'offerDraw':
        if (s.drawOffer === seat) return { ok: false, reason: 'you already offered a draw' };
        return s.drawOffer === null ? { ok: true } : { ok: false, reason: 'your opponent has offered a draw: accept or decline' };
      case 'acceptDraw':
      case 'declineDraw':
        return s.drawOffer === other(seat) ? { ok: true } : { ok: false, reason: 'no draw offer to answer' };
      case 'resign':
        return { ok: true };
      default:
        return { ok: false, reason: 'unknown action' };
    }
  },

  apply(s, a, ctx) {
    const seat = ctx.actor as Seat;
    if (a.type === 'resign') return { ...s, drawOffer: null, result: win(other(seat), `${SEAT_NAMES[seat]} resigned`) };
    if (a.type === 'offerDraw') return { ...s, drawOffer: seat };
    if (a.type === 'declineDraw') return { ...s, drawOffer: null };
    if (a.type === 'acceptDraw') return { ...s, drawOffer: null, result: draw('draw agreed') };

    const legal = legalMoves(s);
    const m = legal.find((x) => startMatches(x, a)) as Move;
    const board = boardAfter(s.board, m, seat);
    const next: Position = { board, turn: other(seat), castling: rightsAfter(s.castling, m), ep: m.kind === 'double' ? (m.from + m.to) / 2 : null };
    const replies = legalMoves(next);
    const check = inCheck(board, next.turn);
    const san = sanOf(m, legal) + (check ? (replies.length === 0 ? '#' : '+') : '');
    const reset = m.type === PAWN || m.capture !== 0;
    const key = positionKey(next, replies);
    const history = reset || next.castling !== s.castling ? [key] : [...s.history, key];
    const mine = s.captured[seat] as readonly number[];
    const gained = m.capture ? [...mine, m.capture] : mine;
    const out: ChessState = {
      ...s,
      ...next,
      halfmove: reset ? 0 : s.halfmove + 1,
      moves: s.moves + 1,
      history,
      sans: [...s.sans, san],
      last: { from: m.from, to: m.to },
      captured: seat === 0 ? [gained, s.captured[1]] : [s.captured[0], gained],
      // Moving is an answer: an offer made to the mover is declined.
      drawOffer: s.drawOffer === other(seat) ? null : s.drawOffer,
    };
    if (replies.length === 0) return { ...out, result: check ? win(seat, 'checkmate') : draw('stalemate') };
    if (insufficientMaterial(board)) return { ...out, result: draw('insufficient material') };
    if (history.filter((k) => k === key).length >= 3) return { ...out, result: draw('threefold repetition') };
    if (out.halfmove >= FIFTY_MOVE_PLIES) return { ...out, result: draw('50-move rule (100 plies without a pawn move or capture)') };
    return out;
  },

  view(s, viewer: Viewer): ChessView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const mine = you !== null && you === s.turn && !s.result;
    const legal: { from: number; to: number; promo?: Promo }[] = [];
    if (mine) {
      for (const m of legalMoves(s)) {
        if (m.promo === 0) legal.push({ from: m.from, to: m.to });
        else if (m.promo === QUEEN) for (const promo of PROMOS) legal.push({ from: m.from, to: m.to, promo });
      }
    }
    return {
      you,
      board: s.board,
      turn: s.result ? null : s.turn,
      legal,
      last: s.last,
      check: inCheck(s.board, s.turn) ? kingSquare(s.board, s.turn) : null,
      sans: s.sans,
      startTurn: s.startTurn,
      captured: s.captured,
      drawOffer: s.drawOffer,
      halfmove: s.halfmove,
      moves: s.moves,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result) return [];
    const out: ChessAction[] = [];
    if (seat === s.turn) {
      for (const m of legalMoves(s)) {
        if (m.promo === 0) out.push({ type: 'move', from: m.from, to: m.to });
        else out.push({ type: 'move', from: m.from, to: m.to, promo: promoLetter(m.promo) });
      }
    }
    if (s.drawOffer === null) out.push({ type: 'offerDraw' });
    else if (s.drawOffer !== seat) out.push({ type: 'acceptDraw' }, { type: 'declineDraw' });
    out.push({ type: 'resign' });
    return out;
  },
};
